/**
 * Turn a thrown mutation error into something worth showing a person.
 *
 * The store's toasts used to pass `err.message` straight through, which meant a
 * failing query surfaced as `Failed query: select "id" from "stores" where …` —
 * a raw SQL string in the operator's face, and an unnecessary hint about the
 * schema. Brand-spec §10.4: say what happened, then what to do; never a stack
 * trace.
 *
 * Known domain errors are worth quoting verbatim — they're written for humans.
 * Anything that smells like infrastructure is replaced by a generic sentence
 * that still tells the operator their work is safe.
 */
import { firstZodIssueMessage } from "../../lib/trpc-errors.ts";

/** Server-side noise that must never reach a toast. */
const INTERNAL_PATTERNS = [
  /failed query/i,
  /relation .* does not exist/i,
  /column .* does not exist/i,
  /syntax error at/i,
  /current_setting/i,
  /econnrefused|etimedout|socket hang up/i,
  /^\s*at\s+\w/m, // a stack frame
  // A stringified Zod issue array. `zodIssues` below is the branch that reads
  // these properly; this is the floor for a payload that arrives without it,
  // because the length test alone doesn't catch them — the one that blocked
  // every product save (#196) was 158 characters and rendered verbatim.
  /^\s*\[\s*\{[\s\S]*"code"\s*:/,
];

/** Domain errors thrown by `commerce/store` — already phrased for people. */
const DOMAIN_HINTS: { match: RegExp; message?: string }[] = [
  {
    // Ordered before the generic SKU hint, and deliberately message-less:
    // `DuplicateVariantSkuError` names the offending SKUs, and on a matrix of
    // a dozen rows "that SKU" leaves the operator hunting for which one. The
    // domain sentence is already phrased for people — pass it through.
    match: /variant sku/i,
  },
  {
    match: /duplicate sku|sku .* already/i,
    message: "That SKU is already in use. Pick a different one.",
  },
  {
    match: /no store for company|nostoreforcompany/i,
    message: "This workspace has no store set up yet.",
  },
  {
    match: /listing not found|listingnotfound/i,
    message: "That product no longer exists. It may have been archived.",
  },
];

function codeOf(err: unknown): string | undefined {
  const data = (err as { data?: { code?: unknown } } | null)?.data;
  return typeof data?.code === "string" ? data.code : undefined;
}

/**
 * @param err   whatever was thrown
 * @param fallback what to say when the cause isn't safe to show
 */
export function storeErrorMessage(
  err: unknown,
  fallback = "Something went wrong on our side. Nothing was saved — try again.",
): string {
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!raw) return fallback;

  for (const hint of DOMAIN_HINTS) {
    // No `message` means the domain's own sentence is better than any rewrite.
    if (hint.match.test(raw)) return hint.message ?? raw;
  }

  // A failed input validation arrives with `message` set to the *stringified*
  // issue array, so the readable copy is in the structured issues rather than
  // the message. Read it from there rather than rendering the array — a form
  // that lets a bad value through is a bug in the form, but the operator should
  // still get the sentence, not the JSON. @see firstZodIssueMessage
  const issue = firstZodIssueMessage(err);
  if (issue) return issue;

  if (codeOf(err) === "INTERNAL_SERVER_ERROR") return fallback;
  if (INTERNAL_PATTERNS.some((p) => p.test(raw))) return fallback;

  // Long messages are almost always machine output rather than a sentence.
  return raw.length <= 160 ? raw : fallback;
}
